import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import {
  IResourceAllocationDataMemberGridColumns,
  IResourseAllocationGridColumns
} from '../type';
import { ViewType } from '../../common/utils/enum';
import { statusMap } from '../../common/utils/helperFunctions';
import NoRecordsOverlay from '../../common/data-no-records';

const cellTypoStyle = {
  fontFamily: 'Be Vietnam Pro',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '24px',
  letterSpacing: '0.5px',
  textAlign: 'left',
  color: '#1A1B1F',
  maxWidth: '220px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

interface ITableData {
  columns: IResourseAllocationGridColumns[];
  isLoading?: boolean;
  view?: ViewType;
  data: IResourceAllocationDataMemberGridColumns[];
}

const ResourceAllocationDataGrid = ({
  columns,
  isLoading,
  data
}: ITableData): JSX.Element => {
  const rows = data.map((item) => ({
    projectName: item.project.projectName,
    projectStatus: item.project.projectStatus,
    hoursPerWeek: item.hoursPerWeek,
    tentativeStartDate: item.roleStartDate
      ? new Date(item.roleStartDate).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })
      : '',
    tentativeEndDate: item.roleEndDate
      ? new Date(item.roleEndDate).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })
      : '',
    projectColorCode: item.project.colorCode
  }));

  const mappedColumns: GridColDef[] = columns.map((col) => {
    if (col.field === 'projectName') {
      return {
        ...col,
        renderCell: (params) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  display: 'inline-block',
                  backgroundColor: params.row.projectColorCode,
                  marginRight: 1,
                  verticalAlign: 'middle'
                }}
              />
              <Typography sx={cellTypoStyle}>{params.value}</Typography>
            </Box>
          );
        }
      };
    }

    if (col.field === 'projectStatus') {
      return {
        ...col,
        renderCell: (params) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  display: 'inline-block',
                  backgroundColor: statusMap(params.value).colorCode,
                  marginRight: 1,
                  verticalAlign: 'middle'
                }}
              />
              <Typography sx={cellTypoStyle}>
                {statusMap(params.value).status}
              </Typography>
            </Box>
          );
        }
      };
    }

    if (col.field === 'hoursPerWeek') {
      return {
        ...col,
        renderCell: (params) => (
          <Typography sx={cellTypoStyle}>{params.value} </Typography>
        )
      };
    }

    return col;
  });

  return (
    <Box
      sx={{
        maxWidth: '100%',
        '& .actions': {
          color: 'text.secondary'
        },
        '& .textPrimary': {
          color: 'text.primary'
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: '#E5F2FF'
        }
      }}
    >
      <DataGrid
        rows={rows ?? []}
        columns={mappedColumns}
        loading={isLoading}
        disableColumnMenu
        disableColumnSelector
        disableVirtualization
        disableSelectionOnClick
        getRowId={(row) => row.projectName}
        autoHeight
        hideFooter
        components={{
          NoRowsOverlay: () => (
            <NoRecordsOverlay
              isLoading={isLoading}
              dataLength={rows.length}
              message="No Allocations Available."
            />
          )
        }}
        sx={{
          minHeight: '230px',
          border: 'none',
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
            width: '7px',
            height: '7px',
            backgroundColor: 'rgba(217, 217, 217, 0.4)'
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(217, 217, 217, 1)',
            borderRadius: '10px'
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#888'
          }
        }}
      />
    </Box>
  );
};

export default ResourceAllocationDataGrid;
